import { useEffect, useState } from 'react';

import {
    COMPOSE_START_DELAY_MS,
    type ComposeStep,
    composeStepDelayMs,
} from '@apps/web/features/learn_phoebe/model/playback';

const TURN_REVEAL_MS = 900;

function prefersReducedMotion(): boolean {
    return (
        typeof window.matchMedia !== 'function' ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

// Advances through the compose steps with per-step pacing; instant under
// reduced motion (and in tests, where matchMedia is unavailable). Runs once
// per mount — the fake chat is remounted per task/replay via its key.
export function useComposePlayback(steps: ReadonlyArray<ComposeStep>): number {
    const [consumedCount, setConsumedCount] = useState(0);
    useEffect(() => {
        // Reset on a new script rather than trusting every caller to remount us.
        setConsumedCount(0);
        if (steps.length === 0 || prefersReducedMotion()) {
            setConsumedCount(steps.length);
            return;
        }
        let consumed = 0;
        let timer: ReturnType<typeof setTimeout>;
        const advance = () => {
            consumed += 1;
            setConsumedCount(consumed);
            const nextStep = steps[consumed];
            if (nextStep === undefined) {
                return;
            }
            timer = setTimeout(advance, composeStepDelayMs(nextStep));
        };
        timer = setTimeout(advance, COMPOSE_START_DELAY_MS);
        return () => clearTimeout(timer);
    }, [steps]);
    return consumedCount;
}

// Reveals the conversation one turn at a time once the composer has sent.
// Under reduced motion (or in tests) the whole script shows at once.
export function useRevealedTurnCount(
    totalTurns: number,
    hasSent: boolean,
): number {
    const [revealedCount, setRevealedCount] = useState(0);
    useEffect(() => {
        if (!hasSent) {
            setRevealedCount(0);
            return;
        }
        if (prefersReducedMotion() || totalTurns <= 1) {
            setRevealedCount(totalTurns);
            return;
        }
        setRevealedCount(1);
        let revealed = 1;
        const interval = setInterval(() => {
            revealed += 1;
            setRevealedCount(revealed);
            if (revealed >= totalTurns) {
                clearInterval(interval);
            }
        }, TURN_REVEAL_MS);
        return () => clearInterval(interval);
    }, [totalTurns, hasSent]);
    return revealedCount;
}
